/**
 * Marketing Automation: the social media campaign of a property (epic ISSUE-3615).
 *
 * Typed on the backend DTOs of backend-platform `platform/.../dto/socialCampaign/` and the module api enums of
 * `module-social-campaign` as of ISSUE-3620 (lifecycle, PR #612 at 7e5660dd1a, which carries ISSUE-3983 and ISSUE-3984), plus
 * the campaign's `story` of ISSUE-3985 (PR #615, a sibling of #612, hence optional), and the visuals of a kind of
 * content of ISSUE-3651 (PR #623).
 * Instants are ISO-8601 strings, `LocalDate` is `YYYY-MM-DD`, `LocalTime` is `HH:mm`, every id a UUID string.
 */

/** DRAFT until its posts are approved, LIVE while the property is marketed, ENDED once stopped. */
export enum CampaignStatus {
  DRAFT = 'DRAFT',
  LIVE = 'LIVE',
  ENDED = 'ENDED',
}

/** How a campaign reads now, derived by the backend from its status, its pause and its posts. */
export enum CampaignPhase {
  DRAFT = 'DRAFT',
  /** Live with a post still ahead. */
  LIVE = 'LIVE',
  /** Live but paused: its scheduled posts are held. */
  PAUSED = 'PAUSED',
  /** Live with nothing ahead, until posts are added or generated. */
  QUIET = 'QUIET',
  ENDED = 'ENDED',
}

/** Stored status of a post; published and failed are per network (ISSUE-3627), not stored here. */
export enum PostStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  /** Its time came while the campaign was paused or ended: never published. */
  SKIPPED = 'SKIPPED',
  /** A slot an order reserved until the ordered content is delivered. */
  WAITING_FOR_CONTENT = 'WAITING_FOR_CONTENT',
}

/**
 * Where a post stands, read by the backend when it answered: its stored status against the clock, the edit freeze
 * and the campaign's hold (paused, or not launched yet). A SCHEDULED or FROZEN post whose time passed since the read
 * is read from the clock (`postDisplayStatus`) until the next read. It also decides what the post editor
 * (ISSUE-3624) still lets the client change.
 */
export enum PostState {
  DRAFT = 'DRAFT',
  WAITING_FOR_CONTENT = 'WAITING_FOR_CONTENT',
  /** Its time ahead, open to changes (held while the campaign is paused or not launched yet). */
  SCHEDULED = 'SCHEDULED',
  /** Inside the edit freeze before its time on a running campaign: about to go out, no longer changed. */
  FROZEN = 'FROZEN',
  /** Its time came while the campaign ran: it goes out, even when the campaign paused or ended since. */
  SENT = 'SENT',
  /** Its time came while the campaign held it: never published, skipped once the campaign resumes, launches or ends. */
  MISSED = 'MISSED',
  /** Not published: skipped by a pause, an end, or approved too late. */
  SKIPPED = 'SKIPPED',
}

/** Place of a post in the property's story, or OWN for a post the client wrote. */
export enum PostRole {
  OVERVIEW = 'OVERVIEW',
  HIGHLIGHT = 'HIGHLIGHT',
  RELAUNCH = 'RELAUNCH',
  OWN = 'OWN',
}

export enum PostOrigin {
  GENERATED = 'GENERATED',
  MANUAL = 'MANUAL',
}

/** Kind of content a campaign draws on; PHOTOS is always included. */
export enum ContentKind {
  PHOTOS = 'PHOTOS',
  VIDEOS = 'VIDEOS',
  VIRTUAL_STAGING = 'VIRTUAL_STAGING',
  FLOOR_PLANS = 'FLOOR_PLANS',
  DRONE = 'DRONE',
}

export enum MediaFormat {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
}

export enum MediaOrigin {
  DELIVERED = 'DELIVERED',
  ORDERED = 'ORDERED',
  UPLOADED = 'UPLOADED',
  AI = 'AI',
}

/** Networks a campaign publishes to, one account each, in network order. */
export enum SocialNetwork {
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  LINKEDIN = 'LINKEDIN',
  TIKTOK = 'TIKTOK',
}

/** The backend's network order, which every list of networks follows. */
export const SOCIAL_NETWORK_ORDER: readonly SocialNetwork[] = [
  SocialNetwork.INSTAGRAM,
  SocialNetwork.FACEBOOK,
  SocialNetwork.LINKEDIN,
  SocialNetwork.TIKTOK,
]

/** What a new campaign publishes to, the backend's default when the creation leaves `networks` out; TikTok waits on ISSUE-3630. */
export const DEFAULT_SOCIAL_NETWORKS: readonly SocialNetwork[] = [
  SocialNetwork.INSTAGRAM,
  SocialNetwork.FACEBOOK,
  SocialNetwork.LINKEDIN,
]

/** Language the posts are written in; independent from the UI language. */
export enum DraftLanguage {
  EN = 'EN',
  DE = 'DE',
}

/** Where the posts' story comes from (ISSUE-3985). */
export enum StoryStatus {
  /** The language model is writing it; the drafts tell the template story meanwhile */
  GENERATING = 'GENERATING',
  /** Written and stored: the untouched drafts were rewritten with it */
  GENERATED = 'GENERATED',
  /** Failed, refused or unanswered after 15 minutes: the campaign tells the template story */
  FALLBACK = 'FALLBACK',
}

/** The AI actions of the post editor on a post's text (ISSUE-3619); one text goes to every network. */
export enum PostTextAction {
  /** Another phrasing of the same step of the story, its closing lines (essentials, hashtags) kept */
  REGENERATE = 'REGENERATE',
  /** The same text, shorter, its closing lines kept */
  SHORTER = 'SHORTER',
  /** Hashtags added at the end of the text, the text itself untouched */
  MORE_HASHTAGS = 'MORE_HASHTAGS',
}

/** Where a post's text stands with its last AI action (ISSUE-3619). */
export enum PostTextStatus {
  /** An AI action is rewriting it: the text shown is the one before, no other action can start */
  PENDING = 'PENDING',
  /** The text is the post's own: no action ran, or the last one was written */
  READY = 'READY',
  /** The last action failed, was refused or was not answered within 5 minutes: the text is kept, the action can be tried again */
  FAILED = 'FAILED',
}

/** What a post that cannot be published lacks (422 body). */
export enum PostNotPublishableReason {
  NO_TEXT = 'NO_TEXT',
  /** Longer than the text limit of the strictest of its networks. */
  TEXT_TOO_LONG = 'TEXT_TOO_LONG',
  NO_NETWORK = 'NO_NETWORK',
  /** None of its networks can publish any of its visuals. */
  NO_PUBLISHABLE_MEDIA = 'NO_PUBLISHABLE_MEDIA',
}

/** The backend storage `VisualType` (a visual's folder), wider than the gallery's `VisualFileType`. */
export type CampaignVisualType =
  | 'RAW'
  | 'RAW_THUMB'
  | 'RAW_WEB'
  | 'POST'
  | 'POST_THUMB'
  | 'POST_WEB'
  | 'POST_THUMB_WATERMARK'
  | 'POST_WEB_WATERMARK'
  | 'POST_EXPORT_ORIGINAL'
  | 'POST_EXPORT_ORIGINAL_THUMB'
  | 'POST_EXPORT_ORIGINAL_WEB'
  | 'POST_EXPORT_PRINT'
  | 'POST_EXPORT_PRINT_THUMB'
  | 'POST_EXPORT_PRINT_WEB'
  | 'POST_EXPORT_WEB'
  | 'POST_EXPORT_WEB_THUMB'
  | 'POST_EXPORT_WEB_WEB'

/** Identity of a delivered visual. */
export interface VisualKeyDTO {
  assignmentPublicId: string
  /** Blob filename with extension */
  filename: string
  visualType: CampaignVisualType
}

/** Stable reference of an asset: its identity triple, format, kind and origin. */
export interface MediaReferenceDTO extends VisualKeyDTO {
  format: MediaFormat
  contentKind: ContentKind
  origin: MediaOrigin
  label: string | null
}

/** One media of a post with short-lived signed URLs. */
export interface PostMediaDTO {
  position: number
  reference: MediaReferenceDTO
  /** Null for a video or a document */
  thumbnailUrl: string | null
  /** The delivered file itself for a video or a document */
  previewUrl: string | null
}

/** One post of a campaign. */
export interface CampaignPostDTO {
  id: string
  role: PostRole
  /** Step of the story the post tells, e.g. overview, hl:garden:0, rl:1; null for OWN */
  storyKey: string | null
  origin: PostOrigin
  status: PostStatus
  /** Where the post stands at the instant of the answer */
  state: PostState
  /** When the post goes out (ISO instant) */
  publishAt: string
  subject: string
  /** One text for every network */
  text: string
  /** Approved in the editor, or written by hand */
  reviewed: boolean
  edited: boolean
  reservedForOrder: boolean
  media: PostMediaDTO[]
  /** In network order */
  networks: SocialNetwork[]
  /**
   * Where the text stands with its last AI action (ISSUE-3619): poll the campaign while PENDING. Absent from a backend
   * without ISSUE-3619, read as READY (`postTextStatusOf`)
   */
  textStatus?: PostTextStatus
  /** The last AI action asked for on the text; null when none ever was */
  textAction?: PostTextAction | null
}

/** Plan sentence "[posts] across [weeks]" and its start. */
export interface CampaignPlanDTO {
  posts: number
  weeks: number
  /** Day the plan starts, in the campaign timezone (YYYY-MM-DD) */
  start: string
}

export interface ContentKindSettingDTO {
  kind: ContentKind
  /** False when the client left the kind out; photos are always included */
  included: boolean
}

/** How the campaign uses one kind of content. */
export interface ContentKindCapacityDTO {
  kind: ContentKind
  available: boolean
  /** Posts showing a visual of this kind, posts waiting for ordered content left out */
  postsUsing: number
  /** Titles of the posts this kind would add once the property has it; empty when available */
  unlocks: string[]
}

/** What the property's content can carry for the campaign. */
export interface CampaignCapacityDTO {
  /** Posts the story writes before a highlight comes back */
  differentPosts: number
  kinds: ContentKindCapacityDTO[]
}

/** The story a language model writes for the campaign from the listing and its visuals (ISSUE-3985). */
export interface CampaignStoryDTO {
  status: StoryStatus
  /** Language the story is written in */
  language: DraftLanguage
  /** When it was asked for (ISO instant) */
  requestedAt: string
  /** When it was answered (stored or refused); null while it is written or when never answered */
  answeredAt: string | null
}

/** The social media campaign of a property. */
export interface SocialCampaignDTO {
  id: string
  status: CampaignStatus
  phase: CampaignPhase
  /** When the live campaign was paused; null while it runs */
  pausedAt: string | null
  /** When the campaign first went live; null while a draft */
  launchedAt: string | null
  /** When the campaign ended; null unless ended */
  endedAt: string | null
  /** Minutes before its time from which a scheduled post can no longer be edited or removed */
  editFreezeMinutes: number
  draftLanguage: DraftLanguage
  /** IANA timezone of the plan and of the posts' times */
  timezone: string
  plan: CampaignPlanDTO
  /** In network order */
  networks: SocialNetwork[]
  contentKinds: ContentKindSettingDTO[]
  excludedVisuals: VisualKeyDTO[]
  /** In publication order; empty until the plan is generated */
  posts: CampaignPostDTO[]
  createdAt: string
  updatedAt: string
  /** On a draft read with its content; null once launched and in the answers of post and lifecycle actions */
  capacity: CampaignCapacityDTO | null
  /**
   * Where the posts' story comes from (ISSUE-3985); null for a campaign that never asked for a generated one,
   * absent from a backend without ISSUE-3985
   */
  story?: CampaignStoryDTO | null
}

/** One usable asset of the property; persist the identity fields, the URLs expire. */
export interface ContentItemDTO extends VisualKeyDTO {
  format: MediaFormat
  contentKind: ContentKind
  origin: MediaOrigin
  label: string | null
  /**
   * The room the visual shows, a backend `RoomLabel` (ISSUE-3636: EXTERIOR, LIVING_ROOM, KITCHEN, … OTHER); null while
   * a photo is not classified, and on a backend without ISSUE-3636
   */
  roomLabel: string | null
  favourite: boolean
  thumbnailUrl: string | null
  previewUrl: string | null
}

export interface ContentKindInventoryDTO {
  kind: ContentKind
  available: boolean
  count: number
  /** In delivery order */
  items: ContentItemDTO[]
}

/** Content a property can feed into a campaign, per kind of content. */
export interface ContentInventoryDTO {
  kinds: ContentKindInventoryDTO[]
}

export interface PostNotPublishableDTO {
  postId: string
  reasons: PostNotPublishableReason[]
}

/** 422 body of approve, approve-all, add, patch and delete when posts that would go out cannot be published. */
export interface PostsNotPublishableDTO {
  /** Never empty */
  posts: PostNotPublishableDTO[]
}

/** POST body: creates the campaign as a draft holding its first plan; only timezone is required. */
export interface CreateSocialCampaignDTO {
  /** IANA timezone the plan and the posts' times are expressed in */
  timezone: string
  /** EN when omitted */
  draftLanguage?: DraftLanguage
  /** 7 posts across 3 weeks when omitted */
  plan?: CampaignPlanRequestDTO
  /** `DEFAULT_SOCIAL_NETWORKS` when omitted */
  networks?: SocialNetwork[]
}

/** PUT .../plan body. Posts: 4, 5, 6, 7, 8, 9, 10 or 12; weeks: 1, 2, 3, 4 or 6. */
export interface CampaignPlanRequestDTO {
  posts: number
  weeks: number
}

/** PUT .../settings body; a field left out keeps its value. Networks: at least one, each once. */
export interface UpdateSocialCampaignSettingsDTO {
  networks?: SocialNetwork[]
  draftLanguage?: DraftLanguage
}

/** PUT .../content-kinds/{kind} body. */
export interface ContentKindInclusionDTO {
  included: boolean
}

/**
 * PUT .../content-kinds/{kind}/visuals body (ISSUE-3651, PR #623): the visuals of the kind left out, each once, taken
 * from the caller's content inventory of the kind (any other visual is a 400); every other visual of the kind the
 * caller is shown is kept. Leaving every visual out leaves the kind out, keeping one brings it back; photos keep one.
 */
export interface ContentKindVisualsDTO {
  excludedVisuals: VisualKeyDTO[]
}

/** POST .../generate body; send `{}` for 4 posts across 2 weeks. Posts: 2, 3, 4, 5, 6 or 8; weeks: 1 to 4. */
export interface GenerateSocialCampaignPostsDTO {
  posts?: number
  weeks?: number
}

/** POST .../posts body: a post written by hand, scheduled at once. */
export interface CreateCampaignPostDTO {
  /** YYYY-MM-DD in the campaign timezone */
  date: string
  /** HH:mm in the campaign timezone, more than the edit freeze ahead */
  time: string
  text: string
  /** At least one, from the property's gallery */
  media: VisualKeyDTO[]
  /** At least one of the campaign's */
  networks: SocialNetwork[]
  /** The first sentence of the text when omitted */
  subject?: string
}

/** PATCH .../posts/{postId} body; a field left out keeps its value. */
export interface UpdateCampaignPostDTO {
  subject?: string
  text?: string
  media?: VisualKeyDTO[]
  networks?: SocialNetwork[]
  /** YYYY-MM-DD; the post keeps its time of day */
  date?: string
  /** HH:mm; the post keeps its day */
  time?: string
}
